import axios from "axios";
import { QueryClient } from "react-query";

// Shared client so non-hook code (for example, plain service helpers) can invalidate cached reads.
export const queryClient = new QueryClient();

// Root keys of reads cached through cachedRequest. Existing useQuery hooks are never touched.
const cachedReadRoots = new Set();
export const registerCachedReadRoot = (root) => cachedReadRoots.add(root);

export const invalidateCachedReads = () =>
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) && cachedReadRoots.has(query.queryKey[0]),
  });

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
// Sign-in, token and telemetry calls do not change portal data.
const NON_DATA_URL = /login\.microsoftonline|oauth|token|argus\.|\/collect(\?|$)/i;
// POST endpoints that only read data (the request body carries the query).
const READ_ONLY_POST_URL = /\/audit-history\/last-updates(\?|$)|graph\.microsoft\.com\/[^?]*\/search\/query(\?|$)/i;

const isDataWrite = (method, url) => {
  const verb = String(method || "GET").toUpperCase();
  const target = String(url || "");
  if (!WRITE_METHODS.has(verb) || NON_DATA_URL.test(target)) return false;
  return !(verb === "POST" && READ_ONLY_POST_URL.test(target));
};

// Listeners for longer-lived caches that should be cleared by writes but kept across page navigation
// (for example the Insights dashboard data).
const dataWriteListeners = new Set();
export const onDataWrite = (listener) => {
  dataWriteListeners.add(listener);
  return () => dataWriteListeners.delete(listener);
};

const handleDataWrite = () => {
  invalidateCachedReads();
  dataWriteListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("Data write listener failed:", error);
    }
  });
};

// Any write (successful or not) makes cached reads stale, so the next read goes to the backend.
// This covers every write path, including ones that do not invalidate explicitly.
axios.interceptors.response.use(
  (response) => {
    if (isDataWrite(response.config?.method, response.config?.url)) handleDataWrite();
    return response;
  },
  (error) => {
    if (isDataWrite(error?.config?.method, error?.config?.url)) handleDataWrite();
    return Promise.reject(error);
  }
);

if (typeof window !== "undefined" && typeof window.fetch === "function" && !window.fetch.invalidatesCachedReads) {
  const originalFetch = window.fetch;
  const fetchThatInvalidatesOnWrite = (input, init) => {
    const isRequestObject = typeof Request !== "undefined" && input instanceof Request;
    const method = init?.method || (isRequestObject ? input.method : "GET");
    const url = isRequestObject ? input.url : String(input);
    const result = originalFetch.call(window, input, init);
    return isDataWrite(method, url) ? result.finally(handleDataWrite) : result;
  };
  fetchThatInvalidatesOnWrite.invalidatesCachedReads = true;
  window.fetch = fetchThatInvalidatesOnWrite;
}
